import * as Icons from "heroicons-react";
import React, { useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { saveAs } from "file-saver";
import { Link, useLocation } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { format } from "date-fns";
import { notify } from "../../Utility/Notify";

const ExportNav = ({ preheader = [], Routes, data, title, printRef }) => {
  let active = window.location.pathname;
  const [tableHeader, setTableHeader] = useState([]);
  const location = useLocation();
  const componentRef = useRef();

  const timeElapsed = Date.now();
  const today = new Date(timeElapsed);

  const formatCellValue = (row, key) => {
    const val = row?.[key];

    if (key === "createdAt" && val) {
      try {
        return format(new Date(val), "MM-dd-yyyy hh:mm a");
      } catch (e) {
        return String(val);
      }
    }

    if (key === "customerName") {
      return val || "Walkaway Customer";
    }

    if (key === "status") {
      if (row?.due) return "Bill Due";
      if (val === "complete") return "Complete";
      return val || "Due";
    }

    if (
      [
        "grossSale",
        "totalReceived",
        "changeAmount",
        "mrpSale",
        "netSale",
        "discount",
        "promo_discount",
        "vat",
        "returnTotal",
      ].includes(key)
    ) {
      const num = parseFloat(val);
      return !isNaN(num) ? new Intl.NumberFormat().format(num) : "0";
    }

    if (key === "totalItem") {
      return val ?? 0;
    }

    if (typeof val === "object" && val !== null) {
      return JSON.stringify(val);
    }

    return val ?? "-";
  };

  const isNumericColumn = (key) =>
    [
      "totalItem",
      "grossSale",
      "totalReceived",
      "changeAmount",
      "mrpSale",
      "netSale",
      "discount",
      "promo_discount",
      "vat",
      "returnTotal",
    ].includes(key);

  const calculateSum = (key) => {
    if (!data || !data.length) return 0;
    return data.reduce((sum, item) => {
      const v = parseFloat(item?.[key]);
      return sum + (isNaN(v) ? 0 : v);
    }, 0);
  };

  // Convert an array of objects to CSV
  const convertArrayOfObjectsToCSV = (array, preheader) => {
    if (!array || !array.length) return null;

    const useDefinedHeaders = tableHeader && tableHeader.length > 0;
    const headers = useDefinedHeaders
      ? tableHeader.map((h) => h.label)
      : Object.keys(array[0]);
    const headerKeys = useDefinedHeaders
      ? tableHeader.map((h) => h.key)
      : Object.keys(array[0]);

    const csvData = [
      ...preheader.map((row) => (Array.isArray(row) ? row.join(",") : row)),
      headers.join(","),
      ...array.map((row) =>
        headerKeys
          .map((fieldName) => {
            const val = formatCellValue(row, fieldName);
            return JSON.stringify(val ?? "") || '""';
          })
          .join(",")
      ),
    ];

    return csvData.join("\r\n");
  };

  const downloadCSV = () => {
    const csvString = convertArrayOfObjectsToCSV(data, preheader);
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `${title || "Export"} - [${today.toDateString()}].csv`);
  };

  const handlePrint = useReactToPrint({
    content: () =>
      printRef && printRef.current ? printRef.current : componentRef.current,
    documentTitle: `${title || "Report"} - [${today.toDateString()}]`,
    pageStyle: `
      @page {
        size: auto;
        margin: 12mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  const handlePrintClick = () => {
    if (!data || data.length === 0) {
      notify("No data available to print", "warning");
      return;
    }
    try {
      handlePrint();
    } catch (err) {
      console.error("Print error, falling back to window.print", err);
      window.print();
    }
  };

  useEffect(() => {
    if (location.pathname === "/sales") {
      const salesHeaders = [
        { label: "Invoice ID", key: "invoiceId" },
        { label: "Total Item", key: "totalItem" },
        { label: "MRP Total", key: "mrpSale" },
        { label: "Discount", key: "discount" },
        { label: "Promo Discount", key: "promo_discount" },
        { label: "Vat", key: "vat" },
        { label: "Return MRP Total", key: "returnTotal" },
        { label: "Gross Total", key: "grossSale" },
        { label: "Net Sale", key: "netSale" },
        { label: "Customer Name", key: "customerName" },
        { label: "Created At", key: "createdAt" },
        { label: "Status", key: "status" },
      ];
      setTableHeader(salesHeaders);
    } else if (
      location.pathname === "/duesale" ||
      location.pathname.includes("duesale")
    ) {
      const dueSalesHeaders = [
        { label: "Invoice ID", key: "invoiceId" },
        { label: "Date", key: "createdAt" },
        { label: "Biller", key: "billerName" },
        { label: "Warehouse", key: "warehouseName" },
        { label: "Customer", key: "customerName" },
        { label: "Items", key: "totalItem" },
        { label: "Gross Total", key: "grossSale" },
        { label: "Paid", key: "totalReceived" },
        { label: "Change", key: "changeAmount" },
        { label: "Status", key: "status" },
      ];
      setTableHeader(dueSalesHeaders);
    } else {
      const excludeKeys = [
        "_id",
        "__v",
        "password",
        "cart",
        "paymentDetails",
        "storeSettings",
        "updatedAt",
        "aamarId",
        "actions",
      ];
      const dynamicHeaders =
        data && data.length > 0
          ? Object.keys(data[0])
              .filter((key) => !excludeKeys.includes(key))
              .map((key) => ({
                label:
                  key.charAt(0).toUpperCase() +
                  key.slice(1).replace(/([A-Z])/g, " $1"),
                key: key,
              }))
          : [];
      setTableHeader(dynamicHeaders);
    }
  }, [location.pathname, Routes, data]);

  return (
    <nav className="nav">
      {Routes?.map((link) => (
        <Link
          key={link.url}
          className={`nav-link ${active === link.url && "active"}`}
          to={link.url}
        >
          <Icons.TableOutline /> {link.label}
        </Link>
      ))}

      <div className="nav-link">
        <Dropdown>
          <Dropdown.Toggle variant="dark" id="dropdown-basic">
            <Icons.DownloadOutline /> Export
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {data && data.length > 0 ? (
              <Dropdown.Item as="button" onClick={downloadCSV}>
                <Icons.DocumentTextOutline size={20} className="me-2" /> CSV
              </Dropdown.Item>
            ) : (
              <Dropdown.Item
                as="button"
                disabled
                style={{ opacity: 0.6, cursor: "not-allowed" }}
              >
                <Icons.DocumentTextOutline size={20} className="me-2" />{" "}
                {data === undefined ? "Loading Data..." : "No Data (CSV)"}
              </Dropdown.Item>
            )}

            <Dropdown.Item as="button" onClick={handlePrintClick}>
              <Icons.PrinterOutline size={20} className="me-2" /> Print
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Hidden printable report container */}
      <div
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: "1000px",
        }}
      >
        <div
          ref={componentRef}
          style={{
            width: "100%",
            backgroundColor: "#fff",
            color: "#000",
            padding: "20px",
            fontFamily: "Arial, sans-serif",
          }}
        >
          {preheader && preheader.length > 0 ? (
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              {preheader.map((row, idx) => {
                const text = Array.isArray(row)
                  ? row.join(" ")
                  : String(row || "");
                if (!text.trim()) return null;
                if (idx === 0) {
                  return (
                    <h2
                      key={idx}
                      style={{
                        margin: "0 0 4px 0",
                        fontWeight: "bold",
                        fontSize: "22px",
                      }}
                    >
                      {text}
                    </h2>
                  );
                }
                if (
                  idx === preheader.length - 2 ||
                  text.toLowerCase().includes("report")
                ) {
                  return (
                    <h4
                      key={idx}
                      style={{
                        margin: "8px 0 4px 0",
                        fontWeight: "bold",
                        fontSize: "16px",
                        textDecoration: "underline",
                      }}
                    >
                      {text}
                    </h4>
                  );
                }
                return (
                  <div
                    key={idx}
                    style={{
                      fontSize: "13px",
                      color: "#444",
                      margin: "2px 0",
                    }}
                  >
                    {text}
                  </div>
                );
              })}
              <div
                style={{
                  fontSize: "11px",
                  color: "#666",
                  marginTop: "6px",
                }}
              >
                Printed At: {new Date().toLocaleString()}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: "0 0 4px 0", fontWeight: "bold" }}>
                {title || "Report"}
              </h2>
              <div style={{ fontSize: "11px", color: "#666" }}>
                Printed At: {new Date().toLocaleString()}
              </div>
            </div>
          )}

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "12px",
              fontFamily: "Arial, sans-serif",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#eaeaea" }}>
                {tableHeader.map((header) => (
                  <th
                    key={header.key}
                    style={{
                      border: "1px solid #999",
                      padding: "6px 8px",
                      textAlign: isNumericColumn(header.key)
                        ? header.key === "totalItem"
                          ? "center"
                          : "right"
                        : header.key === "status"
                        ? "center"
                        : "left",
                      fontWeight: "bold",
                    }}
                  >
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data && data.length > 0 ? (
                data.map((row, rIdx) => (
                  <tr
                    key={row._id || rIdx}
                    style={{
                      backgroundColor: rIdx % 2 === 0 ? "#fff" : "#f9f9f9",
                      pageBreakInside: "avoid",
                    }}
                  >
                    {tableHeader.map((header) => (
                      <td
                        key={header.key}
                        style={{
                          border: "1px solid #ccc",
                          padding: "5px 8px",
                          textAlign: isNumericColumn(header.key)
                            ? header.key === "totalItem"
                              ? "center"
                              : "right"
                            : header.key === "status"
                            ? "center"
                            : "left",
                        }}
                      >
                        {formatCellValue(row, header.key)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={tableHeader.length || 1}
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      border: "1px solid #ccc",
                    }}
                  >
                    No Records Found
                  </td>
                </tr>
              )}
            </tbody>
            {tableHeader.some((h) => isNumericColumn(h.key)) &&
              data &&
              data.length > 0 && (
                <tfoot>
                  <tr
                    style={{
                      backgroundColor: "#e0e0e0",
                      fontWeight: "bold",
                    }}
                  >
                    {tableHeader.map((header, idx) => {
                      if (idx === 0) {
                        return (
                          <td
                            key={header.key}
                            style={{
                              border: "1px solid #999",
                              padding: "6px 8px",
                              textAlign: "left",
                            }}
                          >
                            Total ({data.length})
                          </td>
                        );
                      }
                      if (isNumericColumn(header.key)) {
                        const sum = calculateSum(header.key);
                        return (
                          <td
                            key={header.key}
                            style={{
                              border: "1px solid #999",
                              padding: "6px 8px",
                              textAlign:
                                header.key === "totalItem" ? "center" : "right",
                            }}
                          >
                            {header.key === "totalItem"
                              ? sum
                              : new Intl.NumberFormat().format(
                                  parseFloat(sum.toFixed(2))
                                )}
                          </td>
                        );
                      }
                      return (
                        <td
                          key={header.key}
                          style={{
                            border: "1px solid #999",
                            padding: "6px 8px",
                          }}
                        ></td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
          </table>
        </div>
      </div>
    </nav>
  );
};

export default ExportNav;
